import type { MyVideoVoteWire, VideoVotingPromptResults } from 'data/video-votings';
import { removeVideoVoteAsync, setVideoVoteAsync } from 'data/video-votings';
import { VideoVotingOption } from 'open-modules/zdf/videovoting/videoVotingQuestionElements';
import { useVideoVotingSessionData } from 'open-modules/zdf/videovoting/videoVotingSession';
import React, { useState } from 'react';
import { ScrollView } from 'react-native';
import type { Datastore } from 'util/datastore';
import { useDatastore } from 'util/datastore';
import { fireAndForget } from 'util/hooks';

import type { VideoVotingQuestionPoll, VideoVotingQuestionPollOption } from './videovoting-types';
import { logQuestionDialogEvent } from './videovoting-util';

type VideoVotingQuestionPollDialogProps = {
    question: VideoVotingQuestionPoll;
    results: VideoVotingPromptResults | undefined;
    ownVote: MyVideoVoteWire | null;
};

export function VideoVotingQuestionPollDialog({ question, results, ownVote }: VideoVotingQuestionPollDialogProps) {
    const datastore = useDatastore();
    const session = useVideoVotingSessionData();
    const [hasInteracted, setHasInteracted] = useState(false);

    // Pressing the option already chosen takes the vote back; pressing another
    // casts or moves it. The server decides whether a row exists, so casting
    // and changing are the same call.
    const voteForOption = (option: VideoVotingQuestionPollOption) => {
        setHasInteracted(true);
        const votingKey = session.votingKey;
        if (!votingKey) return;

        if (ownVote && option.key === ownVote.optionKey) {
            fireAndForget(removeVideoVoteAsync(datastore, { votingKey, questionKey: question.key }));
            logPollVoteEvent({ datastore, question, option, eventType: 'videovoting-revoke-vote' });
            return;
        }

        fireAndForget(
            setVideoVoteAsync(datastore, {
                votingKey,
                questionKey: question.key,
                optionKey: option.key,
                anonymous: session?.anonymous ?? false,
            }),
        );
        logPollVoteEvent({ datastore, question, option, eventType: 'videovoting-vote' });
    };

    return (
        <ScrollView>
            {question.options.map((item) => {
                const optionResults = results?.options.find((option) => option.optionKey === item.key);
                return (
                    <VideoVotingOption
                        allVoteCount={results?.total ?? 0}
                        key={item.key}
                        onSelect={() => voteForOption(item)}
                        optionText={item.text}
                        selected={ownVote?.optionKey === item.key}
                        showResults={hasInteracted || ownVote != null}
                        voteCount={optionResults?.count ?? 0}
                        voters={optionResults?.voters ?? []}
                        votingEnabled
                    />
                );
            })}
        </ScrollView>
    );
}

type logPollVoteEventParams = {
    datastore: Datastore;
    question: VideoVotingQuestionPoll;
    option: VideoVotingQuestionPollOption;
    eventType: string;
};
function logPollVoteEvent({ datastore, question, option, eventType }: logPollVoteEventParams) {
    logQuestionDialogEvent({
        datastore,
        question,
        eventType,
        data: {
            optionKey: option.key,
            optionText: option.text,
        },
    });
}
